import React, { FC, useEffect, memo, useState, useCallback } from "react";

import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@apollo/client";
import axios from "axios";
import { createUseStyles } from "react-jss";
import CampusMap from "Components/CampusMap";
import { Message } from "Components/Toast";
import Modal from "HOC/Modal";
import CIF from "Components/CIF";
import { Loading } from "Components/Loading";
import { areEqual, areEqualShallow } from "Utils/areEqual";
import { Directions } from "Containers/Directions";
import { setSuggestedTrails, Trails } from "Containers/Trails";
import {
  setShowMediaViewer,
  setMediaParam,
  setDisplay,
  setShowMediaObj,
} from "Containers/MediaViewer";
import { setCurrentView } from "Containers/MapSwitcher/mapSwitcher.Actions";
import { MapContext } from "Context/MapContext";

import {
  Destinations,
  setDestinationsView,
  setSelectedDestinations,
} from "Containers/Destinations";

import {
  setCurrentStop,
  StopState,
  setStopState,
  setStops,
  setPersonalizedStops,
} from "Containers/Stops";
import {
  SearchAIPrompt,
  setShowSearchAIPrompt,
  setStopLabels,
} from "Containers/SearchAI";

import { sha512DigestHex } from "Utils/ie11SafeCrypto";
import { setIsRegistered } from "HOC/YouVisitAnalytics/store/actions";
import {
  checkIsDestinationsViewActive,
  checkIsDirectionsViewActive,
  checkIsTrailsViewActive,
} from "Utils/utils";
import withRetry from "Utils/retry";
import { stopChangeTrigger } from "Containers/Stops/currentStop.Actions";
import { setDestinationsViewTrigger } from "Containers/Destinations/actions";
import { pauseAudio } from "Containers/AudioPlayer/AudioPlayerActions";
import { getCenterOfShape } from "../../map/coordinates_v3";
import { IRootState } from "../../../store/rootState";
import { StoreUserIdentifier } from "../../analyticsManager";
import { useRegFormOpenCloseTracker } from "../../analyticsManager/events/registration";
import GoogleMap from "../../map/GoogleMap";
import { setShowSearchOptions, setView, setModules, panToStop } from ".";
import { Tour, Point } from "./gqlInterface";
import locationGQL from "./gqlQuery";
import stopStateStyles from "../../stopStateStyles";
import { LOCATION_MODULES } from "../../constants";
import styles from "./styles";
import { useCheckIsCif } from "./useCheckIsCif";
import { useURLSync } from "../../hooks/useURLSync";

/**
 * Check whether we should loop this tour for "points" data.
 */
const shouldLoopTour = (tour, currentTourId: string | null) =>
  tour.tourid === currentTourId || (!currentTourId && tour.status === "live");

/**
 * @param trailPoint - point object from data.locations.tours.trails
 */
const getStandardMapStopThumbnail = (trailPoint) => {
  return `${trailPoint.dir}1024_${trailPoint.orientation.toLowerCase()}.jpg`;
};

const getInteractiveMapStopThumbnail = (
  mapStop: any,
  mediaStop: any,
  tours: any[],
  currentTourId: string | null
) => {
  let thumbnail = mapStop.panoThumb || mapStop.photoThumb || mapStop.videoThumb;
  if (mediaStop.main_media_type) {
    switch (mediaStop.main_media_type) {
      case "photo":
        thumbnail =
          mediaStop.photos.find((pho) => pho.id === mediaStop.main_media_id)
            ?.smallurl || thumbnail;
        break;
      case "panorama":
        thumbnail =
          mediaStop.panoramas.find((pan) => pan.id === mediaStop.main_media_id)
            ?.smallurl || thumbnail;
        break;
      case "video":
        thumbnail =
          mediaStop.videos.find((vid) => vid.id === mediaStop.main_media_id)
            ?.smallurl || thumbnail;
        break;
      default:
        break;
    }
  } else {
    // Resolve URL from Point
    let point: Point;
    tours.forEach((tour: Tour) => {
      if (shouldLoopTour(tour, currentTourId)) {
        tour.trails.forEach((trail) => {
          trail.points.forEach((p) => {
            if (p.stopid === mediaStop.stopid) {
              point = p;
            }
          });
        });
      }
    });

    if (point) {
      thumbnail = getStandardMapStopThumbnail(point);
    }
  }
  return thumbnail;
};

export const isInteractiveMap = (
  modules: Record<string, any>[],
  originalSource: string,
  platform: string
) => {
  const platformKey =
    platform === "webapp" ? `mobile_${originalSource}` : originalSource;
  const iMapModule = modules?.find(
    (module) => module.moduleid === LOCATION_MODULES.FULL_MAP
  );
  const directionsModule = modules?.find(
    (module) => module.moduleid === LOCATION_MODULES.DIRECTIONS
  );
  return iMapModule && iMapModule[platformKey] === "1" && !!directionsModule;
};

const drawStop = (gmap: GoogleMap, mapStop, showStopInfo, showIMap) => {
  let mapObjs;
  if (showIMap) {
    mapObjs = gmap.drawDestination(mapStop, stopStateStyles.DEFAULT, () => {
      showStopInfo(mapStop, gmap);
    });
  } else {
    // want to use default google map markers if not full IMap experience
    mapObjs = gmap.drawDestination(
      mapStop,
      {
        strokeColor: stopStateStyles[StopState.Gmap]?.strokeColor,
        fillColor: stopStateStyles[StopState.Gmap]?.fillColor,
        markerIcon: stopStateStyles[StopState.Gmap]?.marker,
      },
      () => {}
    );
  }
  const { marker, overlay, polygon } = mapObjs;
  return { marker, overlay, polygon };
};

export const combineAndDrawStopsForInteractiveMap = (
  locationsData,
  gmap: GoogleMap | null,
  showStopInfo,
  currentTourId: string | null,
  platform?: string | null,
  originalSource?: string | null
) => {
  const mapStops = locationsData?.interactivemaps?.[0]?.stops ?? [];
  const mediaStops = locationsData?.stops ?? [];
  const tours = locationsData?.tours ?? [];
  const modules = locationsData?.modules ?? [];

  // store common stops from both arrays //
  const combinedStops = [];

  mapStops.forEach((mpStop) => {
    const mapStop = { ...mpStop };
    const mediaStop = mediaStops.find((stop) => stop.stopid === mapStop.id) || {
      stopid: mapStop.id,
    };

    // Validate our stop can be drawn
    mapStop.hasValidLatLng = Boolean(mapStop.realLat && mapStop.realLng);
    if (!mapStop.hasValidLatLng && gmap && !gmap.isCustomTiledMap) {
      mapStop.realLat = mapStop.startLat;
      mapStop.realLng = mapStop.startLng;
      mapStop.hasValidLatLng = Boolean(mapStop.realLat && mapStop.realLng);
    }
    if (mapStop.hasValidLatLng) {
      // gmap can be null if this function is called from ADACampusMap.
      const drawnStop = gmap
        ? drawStop(
            gmap,
            mapStop,
            showStopInfo,
            isInteractiveMap(modules, originalSource, platform)
          )
        : {};
      const thumbnail = getInteractiveMapStopThumbnail(
        mapStop,
        mediaStop,
        tours,
        currentTourId
      );
      if (thumbnail) {
        combinedStops.push({
          ...mapStop,
          ...mediaStop,
          ...drawnStop,
          state: StopState.Default,
          thumbnail,
        });
      }
    }
  });

  combinedStops.sort((a: any, b: any) => a.title.localeCompare(b.title));
  return combinedStops;
};

export const combineStopsForStandardMap = (
  locationsData,
  currentTourId: string | null
) => {
  const trailPoints = {};
  const tours = locationsData?.tours ?? [];
  tours.forEach((tour) => {
    if (shouldLoopTour(tour, currentTourId)) {
      const trails = tour.trails ?? [];
      trails.forEach((trail) => {
        const points = trail.points ?? [];
        points.forEach((point) => {
          if (point.stopid !== null || point.stopid !== undefined) {
            trailPoints[point.stopid] = point;
          }
        });
      });
    }
  });

  const mediaStops = locationsData?.stops ?? [];

  const combinedStops = mediaStops
    .map((stop) => {
      const trailPoint = trailPoints[stop.stopid];
      if (!trailPoint) {
        return undefined;
      }

      const { latitude, longitude, ...restTrailPoint } = trailPoint;

      const { descriptions, ...restStop } = stop;
      const englishDescription = descriptions.find(
        (d) => d.language === "English"
      );

      return {
        id: stop.stopid,
        fullMapDescription: englishDescription?.description ?? "",
        thumbnail: trailPoint
          ? getStandardMapStopThumbnail(trailPoint)
          : undefined,
        panoThumb: stop?.panoramas?.[0]?.smallurl,
        photoThumb: stop?.photos?.[0]?.smallurl,
        videoThumb: stop?.videos?.[0]?.smallurl,
        realLat: latitude,
        realLng: longitude,
        customLat: latitude,
        customLng: longitude,
        state: StopState.Default,
        ...restStop,
        ...restTrailPoint,
      };
    })
    .filter((stop) => stop && stop.realLat && stop.realLng);

  return combinedStops;
};

// Only show map type switcher if tiles exist, and map is interactive map (not standard map).
const checkShowSwitcher = (
  locationsData,
  originalSource,
  platform
): boolean => {
  const tiles = locationsData?.interactivemaps?.[0]?.tiles ?? [];
  return (
    tiles.length > 0 &&
    isInteractiveMap(locationsData?.modules ?? [], originalSource, platform)
  );
};

export const useShowCif = (
  dispatch,
  isCif,
  instId?,
  locId?,
  userkey?,
  tourid?,
  fetchPersonalizedStops?
) => {
  const [showCif, setShowCif] = useState(false);
  const { trackRegFormOpened, trackRegFormClosed } = useRegFormOpenCloseTracker(
    isCif
  );

  const onRegisterClick = useCallback(() => {
    trackRegFormOpened();
    setShowCif(true);
  }, [trackRegFormOpened]);

  const onCifClose = useCallback(() => {
    trackRegFormClosed("Exited");
    setShowCif(false);
  }, [trackRegFormClosed]);

  const onCifComplete = useCallback(() => {
    trackRegFormClosed("Complete");

    // 1500ms so the animation of the form being completed can play //
    setTimeout(() => {
      setShowCif(false);
    }, 1500);
    dispatch(setIsRegistered());
  }, [dispatch, trackRegFormClosed]);

  const onPersonalizationComplete = () => {
    // poll for completion of personalization job, up to 6 times
    withRetry({
      retries: 6,
      delay: 500,
      backOffCoefficient: 2,
      fn: () => {
        return new Promise((resolve, reject) => {
          axios
            .get(
              `${process.env.REACT_APP_API_BASE_URL}api/v2/institutions/${instId}/locations/${locId}/personalized-stops/me/status`,
              { params: { userkey, tour_id: tourid } }
            )
            .then((response) => {
              const { live_updates: liveUpdates } = response.data.resources;
              const completedUpdate = liveUpdates.some(
                (step) => step.completed_ranking === true
              );
              if (completedUpdate) {
                resolve(completedUpdate);
              } else {
                reject();
              }
            })
            .catch((error) => {
              reject(error);
            });
        });
      },
      onSuccess: () => {
        // on success make another request to get personalized stops
        fetchPersonalizedStops();
      },
    });
  };

  return {
    showCif,
    onRegisterClick,
    onCifClose,
    onCifComplete,
    onPersonalizationComplete,
  };
};

interface Props {}

const campusMapContainer: FC<Props> = memo(() => {
  // Styles //
  const useStyles = createUseStyles(styles);
  const classes = useStyles(styles);

  // Keep URL in sync with navigation state
  useURLSync();

  // useSelectors //
  const {
    locId,
    instId,
    platform: platformShortName,
    tourid,
    linkParams,
  } = useSelector((state: IRootState) => state.tour);
  const {
    analytics: { userkey, platform, sessionId, originalSource, token },
    registration: { formType },
    isReady: analyticsIsReady,
    registration,
  } = useSelector((state: IRootState) => state.analytics, areEqual);

  const { view, showSearchOptions, panToStopId } = useSelector(
    ({ searchOptionsView }: any) => searchOptionsView,
    areEqual
  );
  const { showDestinations } = useSelector(
    (selectors: any) => selectors.destinations,
    areEqual
  );
  const currentStop = useSelector(
    (selectors: any) => selectors.currentStop.currentStop,
    areEqual
  );
  const { modules } = useSelector(
    (selectors: any) => selectors.locationModulesMatrix,
    areEqual
  );
  const { personalizedStops: personalizedStopsList } = useSelector(
    ({ personalizedStops }: any) => personalizedStops,
    areEqual
  );

  const stops = useSelector(({ stops: stopsList }: any) => stopsList, areEqual);

  const switcher = useSelector((store: any) => store.mapSwitcher);

  const hasSearchAI = modules?.some(
    (module) => module.moduleid === LOCATION_MODULES.SEARCH_AI
  );

  const { audio, isPlaying } = useSelector((state: any) => state.audioPlayer);

  const { showSearchAIPrompt } = useSelector((state: any) => state.searchAI);
  // useStates //
  const [showPrompt, setShowPrompt] = useState(false);
  const [trailsLength, setTrailsLength] = useState(0);
  const [locationBounds, setLocationBounds] = useState(null);
  const [actionButtonsConfig, setActionButtonsConfig] = useState({
    register: {},
    scheduleVisit: {},
    applyOnline: {
      typeid: undefined,
    },
    learnMore: {},
    showRegistration: true,
  });
  const [defaultActionbuttonsConfig, setDefaultActionButtonsConfig] = useState({
    register: {},
    scheduleVisit: {},
    applyOnline: {},
    learnMore: {},
  });
  const [googleMap, setGoogleMap] = useState(null);
  const [trailsOption, setTrailsOption] = useState(true);
  const [innerHeight, setInnerHeight] = useState(window.innerHeight);
  const [campusMapStops, setCampusMapStops] = useState([]);

  // Dispatches //
  const dispatch = useDispatch();

  // Animations //
  const anims = {
    open: {
      x: "0%",
      zIndex: 1,
      transition: {
        ease: [0.28, 0.57, 0.2, 1],
        duration: 0.5,
      },
    },
    closed: {
      zIndex: 0,
      x: "-100%",
      transition: {
        ease: "easeIn",
        duration: 0.25,
      },
    },
  };

  // Functions //
  const getOption = (showSwitcher, setShowSearchPrompt) => {
    return (
      <>
        <motion.div
          initial={{ x: "-100%" }}
          animate={checkIsDestinationsViewActive(view) ? "open" : "closed"}
          variants={anims}
          className={classes.views}
        >
          <Destinations
            trailsLength={trailsLength}
            googleMap={googleMap}
            showSwitcher={showSwitcher}
            showSearchAI={hasSearchAI}
            setShowSearchPrompt={setShowSearchPrompt}
          />
        </motion.div>
        <motion.div
          initial={{ x: "-100%" }}
          animate={checkIsTrailsViewActive(view) ? "open" : "closed"}
          variants={anims}
          className={classes.views}
        >
          <Trails
            trailsOption={trailsOption}
            setTrailsOption={setTrailsOption}
            showSwitcher={showSwitcher}
          />
        </motion.div>
        <motion.div
          initial={{ x: "-100%" }}
          animate={checkIsDirectionsViewActive(view) ? "open" : "closed"}
          variants={anims}
          className={classes.views}
        >
          <Directions
            showSwitcher={showSwitcher}
            trailsCount={trailsLength}
            locationBounds={locationBounds}
          />
        </motion.div>
      </>
    );
  };

  const showStopInfo = useCallback(
    (stop: any, gmap: GoogleMap) => {
      dispatch(setStopState([stop.id], StopState.Selected));

      dispatch(setCurrentStop(-1));

      dispatch(setCurrentStop(stop.id, stopChangeTrigger.MAP_CLICK));

      // Hide the Destinations Drop down
      // TODO:  SHould this be here in maps?  Or should I have something general that will hide the lists all at once
      dispatch(setDestinationsView(false));
      // TODO:  SHould this be here in maps?  Or should I have something general that will hide the lists all at once

      dispatch(setSelectedDestinations(stop.panoThumb));

      // eslint-disable-next-line no-undef
      const latLng = gmap.isCustomTiledMap
        ? new window.google.maps.LatLng(stop.customLat, stop.customLng)
        : new window.google.maps.LatLng(stop.realLat, stop.realLng);
      gmap.panTo(latLng);
    },
    [dispatch]
  );

  // useCallback //
  const showHideList = useCallback(() => {
    dispatch(
      setDestinationsView(true, setDestinationsViewTrigger.BUTTON_CLICK)
    );
    // If we are on mobile, we want to show or hide the search options, when the close icon is clicked.

    // Follow this method down and see if it's called with useCallback
    // dispatch(setShowSearchOptions(!showSearchOptions));
    // When we reach this point we clicked on the showMobile button, we want to show the SearchOptions.
    dispatch(setShowSearchOptions(!showSearchOptions));
    dispatch(setView("destinations"));

    if (!showDestinations) {
      dispatch(setCurrentStop(-1));
      dispatch(setStopState([currentStop], StopState.Default));
    }
  }, [dispatch, showDestinations, currentStop, showSearchOptions]);

  const showTrails = () => {
    if (switcher.currentView === "campusmap") {
      setShowPrompt(true);
      dispatch(setCurrentView("mapview", false));
    }
    dispatch(setShowSearchOptions(!showSearchOptions));
    dispatch(setView("trails"));
    if (!showDestinations) {
      dispatch(setCurrentStop(-1));
      dispatch(setStopState([currentStop], StopState.Default));
    }
  };

  const fetchPersonalizedStops = () => {
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}api/v2/institutions/${instId}/locations/${locId}/personalized-stops/me`,
        {
          params: {
            userkey,
            tour_id: tourid,
          },
        }
      )
      .then((d) => {
        if (!personalizedStopsList) {
          const personalizedStops = d.data.resources.stop_ids;
          const filteredStops = personalizedStops?.filter((personalizedStop) =>
            stops?.some((stop) => stop?.id === personalizedStop?.stopid)
          );
          dispatch(setPersonalizedStops(filteredStops));
        }
      });
  };

  // GraphQL Queries //
  const { data /* loading, error */ } = useQuery(locationGQL, {
    variables: {
      inst_id: instId,
      loc_id: locId,
    },
    fetchPolicy: "cache-and-network",
    onCompleted: (response) => {
      // Saves trails so data is ready when trails is active //
      dispatch(
        setSuggestedTrails(response?.locations?.interactivemaps[0].customTrails)
      );
      // Get modules from data //
      const allModules = response?.locations?.modules;
      // Store modules //
      dispatch(setModules(allModules));
    },
  });

  // determine if form is legacy or new CIF //
  const isCif = useCheckIsCif(
    data?.locations?.modules ?? [],
    platformShortName,
    originalSource
  );

  const {
    showCif,
    onRegisterClick,
    onCifClose,
    onCifComplete,
    onPersonalizationComplete,
  } = useShowCif(
    dispatch,
    isCif,
    instId,
    locId,
    userkey,
    tourid,
    fetchPersonalizedStops
  );

  const isCampusMapViewAvailable =
    data?.locations?.interactivemaps[0]?.tiles.length > 0;

  // useEffects //
  useEffect(() => {
    setInnerHeight(window.innerHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.innerHeight]);
  useEffect(() => {
    if (googleMap) {
      if (switcher.currentView === "campusmap" && isCampusMapViewAvailable) {
        googleMap.setMapType(GoogleMap.MAP_TYPE.COORDINATE, stops);
      } else {
        googleMap.setMapType(GoogleMap.MAP_TYPE.ROADMAP, stops);
      }
    }
  }, [googleMap, isCampusMapViewAvailable, stops, switcher.currentView]);

  // this is to identify the scene state for automation testing
  useEffect(() => {
    stops.forEach((stop: any) => {
      // eslint-disable-next-line no-param-reassign
      if (stop.overlay) stop.overlay.content.dataset.sceneState = stop.state;
    });
  }, [stops]);

  useEffect(() => {
    // Action Button Logic
    if (data?.locations) {
      const {
        actionButtons: {
          register: { name: registerName, bgColor, fontColor },
          actions,
          defaultActions,
        },
      } = data?.locations;

      const learnMore = actions.find((action) => action.actionid === "action1");
      const scheduleVisit = actions.find(
        (action) => action.actionid === "action2"
      );
      const applyOnline = actions.find(
        (action) => action.actionid === "action3"
      );

      setActionButtonsConfig({
        register: {
          click: onRegisterClick,
          name: registerName,
          bgColor,
          fontColor,
        },
        scheduleVisit,
        applyOnline,
        learnMore,
        showRegistration: !registration.hasRegisteredUser,
      });

      const defaultRegister = defaultActions.find(
        (action) => action.actionid === "register"
      );
      const defaultLearnMore = defaultActions.find(
        (action) => action.actionid === "action1"
      );
      const defaultScheduleVisit = defaultActions.find(
        (action) => action.actionid === "action2"
      );
      const defaultApplyOnline = defaultActions.find(
        (action) => action.actionid === "action3"
      );
      setDefaultActionButtonsConfig({
        register: defaultRegister,
        scheduleVisit: defaultScheduleVisit,
        applyOnline: defaultApplyOnline,
        learnMore: defaultLearnMore,
      });
    }
  }, [data, registration.hasRegisteredUser, onRegisterClick]);

  const prepareInteractiveMapStops = useCallback(
    (element, locationsData) => {
      const {
        interactivemaps: [
          {
            minLat,
            minLng,
            centerLat,
            centerLng,
            maxLat,
            maxLng,
            zoom,
            stops: mapStops,
            customTrails,
          },
        ],
      } = locationsData;

      setLocationBounds({ minLat, minLng, maxLat, maxLng });

      const gmap = new GoogleMap(
        element,
        locationsData?.interactivemaps[0]?.tiles,
        { lat: minLat, lng: minLng },
        { lat: maxLat, lng: maxLng },
        locationsData?.interactivemaps[0]?.mapMarkers,
        // mapOptions Override
        {
          center: { lat: centerLat, lng: centerLng },
          minZoom: zoom - 2,
          zoom,
          maxZoom: zoom + 2,
        }
      );
      setGoogleMap(gmap);
      setCampusMapStops(mapStops);

      if (mapStops) {
        const combinedStops = combineAndDrawStopsForInteractiveMap(
          locationsData,
          gmap,
          showStopInfo,
          tourid,
          platform,
          originalSource
        );
        dispatch(setStops(combinedStops));
        setTrailsLength(customTrails.length);
      }

      if (
        modules?.some(
          (module) => module.moduleid === LOCATION_MODULES.PERSONALIZATION_AI
        ) &&
        tourid
      ) {
        // check status of personalization job
        axios
          .get(
            `${process.env.REACT_APP_API_BASE_URL}api/v2/institutions/${instId}/locations/${locId}/personalized-stops/me/status`,
            { params: { userkey, tour_id: tourid } }
          )
          .then((response) => {
            const { live_updates: liveUpdates } = response.data.resources;
            if (liveUpdates) {
              const completedUpdate = liveUpdates.some(
                (step) => step.completed_ranking === true
              );
              if (completedUpdate) {
                fetchPersonalizedStops();
              }
            }
          });
      }

      if (linkParams?.stopid) {
        if (currentStop === -1) {
          const newStop = linkParams.stopid;
          const currStop = mapStops.find((stop) => stop.id === newStop);
          // only want to pan if the stop is valid
          if (currStop) {
            const latLng = gmap.isCustomTiledMap
              ? new window.google.maps.LatLng(
                  currStop.customLat,
                  currStop.customLng
                )
              : new window.google.maps.LatLng(
                  currStop.realLat,
                  currStop.realLng
                );
            gmap.panTo(latLng);
          }
        }
        if (linkParams?.media) {
          let mediaType = linkParams.media.split(":")[0];
          const mediaId = linkParams.media.split(":")[1];
          if (mediaType === "pano") mediaType = "panorama"; // expects pano to be panorama
          dispatch(setShowMediaViewer(mediaType));
          dispatch(setMediaParam(mediaId));
          dispatch(setDisplay(true));
          dispatch(setShowMediaObj(true));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, showStopInfo, tourid, platform, originalSource, modules]
  );

  const prepareStandardMapStops = useCallback(
    (element, locationsData) => {
      const combinedStops = combineStopsForStandardMap(locationsData, tourid);

      // find center for standard map
      const stopPoints = combinedStops.map((stop) => ({
        x: stop.realLng,
        y: stop.realLat,
      }));
      const centerPoint = getCenterOfShape(stopPoints);

      const zoom = locationsData?.interactivemaps[0]?.zoom ?? 16;

      const gmap = new GoogleMap(
        element,
        locationsData?.interactivemaps[0]?.tiles,
        null,
        null,
        locationsData?.interactivemaps[0]?.mapMarkers,
        {
          center: { lat: centerPoint.y, lng: centerPoint.x },
          minZoom: zoom - 2,
          zoom,
          maxZoom: zoom + 2,
        }
      );

      const drawnCombinedStops = combinedStops.map((stop) => {
        const drawnStop = drawStop(
          gmap,
          stop,
          showStopInfo,
          isInteractiveMap(modules, originalSource, platform)
        );
        return {
          ...stop,
          ...drawnStop,
        };
      });

      setGoogleMap(gmap);
      dispatch(setStops(drawnCombinedStops));
    },
    [dispatch, showStopInfo, tourid, modules, originalSource, platform]
  );

  const setShowSearchPrompt = useCallback(() => {
    if (audio && isPlaying) {
      audio.pause();
      dispatch(pauseAudio());
    }
    dispatch(setShowSearchAIPrompt());
  }, [dispatch, audio, isPlaying]);

  useEffect(() => {
    if (data?.locations) {
      const element = document.getElementById("campusMap");
      const locationModules = data?.locations?.modules ?? [];

      if (
        locationModules.some(
          (module) => module.moduleid === LOCATION_MODULES.LABELS
        )
      ) {
        axios
          .get(
            `${process.env.REACT_APP_API_BASE_URL}api/v2/institutions/${instId}/locations/${locId}/stops/labels?size=999`
          )
          .then((response) => {
            const labels = response.data?.resources?.data?.reduce(
              (acc, label) => {
                const stopId = label.stop_id;
                if (!acc[stopId]) {
                  acc[stopId] = [label.label_name];
                } else if (acc[stopId].length < 3) {
                  acc[stopId].push(label.label_name);
                }
                return acc;
              }
            );
            dispatch(setStopLabels(labels));
          });
      }
      // if full

      if (isInteractiveMap(locationModules, originalSource, platform)) {
        prepareInteractiveMapStops(element, data?.locations);
      } else {
        prepareStandardMapStops(element, data?.locations);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    originalSource,
    platform,
    tourid,
    prepareInteractiveMapStops,
    prepareStandardMapStops,
  ]);

  useEffect(() => {
    if (linkParams?.stopid) {
      // For some reason Scene component would not display when setting stopState along with panTo() in prepareInteractiveMapStops callback so must add here
      if (campusMapStops.length > 0 && currentStop === -1) {
        const newStop = linkParams.stopid;
        const currStop = campusMapStops.find((stop) => stop.id === newStop);
        if (currStop) {
          dispatch(setStopState([currStop.id], StopState.Selected));
          dispatch(setCurrentStop(currStop.id));
          dispatch(setDestinationsView(false));
          dispatch(setSelectedDestinations(currStop.panoThumb));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, linkParams.stopid, campusMapStops, showStopInfo]);

  // useEffect to handle map panning triggered by Redux action
  useEffect(() => {
    if (panToStopId && googleMap && campusMapStops?.length > 0) {
      const stopToFind = campusMapStops.find((stop) => stop.id === panToStopId);
      if (stopToFind) {
        const latLng = googleMap.isCustomTiledMap
          ? new window.google.maps.LatLng(
              stopToFind.customLat,
              stopToFind.customLng
            )
          : new window.google.maps.LatLng(
              stopToFind.realLat,
              stopToFind.realLng
            );
        googleMap.panTo(latLng);

        // Reset the panToStopId to null to prevent repeated panning
        dispatch(panToStop(null));
      }
    }
  }, [panToStopId, googleMap, campusMapStops, dispatch]);

  useEffect(() => {
    // to identify user in tracking services (Gainsight) used with AnalyticsManager
    if (analyticsIsReady && userkey && data) {
      sha512DigestHex(userkey).then((userkeyHex) =>
        dispatch(
          StoreUserIdentifier({
            id: userkeyHex,
            institution_id: instId,
            // eslint-disable-next-line camelcase
            institution_name: data?.locations?.inst_name,
            location_id: locId,
            location_name: data?.locations?.name,
          })
        )
      );
    }
  }, [dispatch, analyticsIsReady, userkey, instId, locId, data]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === " ") {
        setShowSearchPrompt();
      }
    };

    document.addEventListener("keypress", handleKeyPress);

    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [setShowSearchPrompt]);

  const showSwitcher = checkShowSwitcher(
    data?.locations,
    originalSource,
    platform
  );

  const typeid = actionButtonsConfig.applyOnline?.typeid;

  return (
    <>
      {!data && (
        <div
          style={{
            zIndex: 9999999,
            backgroundColor: "#fff",
            position: "fixed",
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Loading />
        </div>
      )}
      <MapContext.Provider
        value={{
          instId,
          locId,
        }}
      >
        <CampusMap
          showSwitcher={showSwitcher}
          setZoom={googleMap?.setZoom}
          actionButtonsConfig={actionButtonsConfig}
          defaultActionButtonsConfig={defaultActionbuttonsConfig}
          showHideList={showHideList}
          showTrails={showTrails}
          trailsOption={trailsOption}
          trailsCount={trailsLength}
          showSearchOptions={showSearchOptions}
          mapObj={googleMap}
          locId={locId}
          currentView={switcher.currentView}
          view={view}
          // @ts-ignore
          innerHeight={innerHeight}
          showIMapFeatures={isInteractiveMap(modules, originalSource, platform)}
          setShowSearchAIPrompt={setShowSearchPrompt}
          showSearchAI={hasSearchAI}
        >
          {getOption(showSwitcher, setShowSearchPrompt)}
        </CampusMap>
      </MapContext.Provider>

      {showCif && (
        <Modal onClose={onCifClose}>
          <CIF
            isCif={isCif}
            locId={locId}
            instId={instId}
            tourId={tourid}
            userKey={userkey}
            sessionId={sessionId}
            onFormCompleted={onCifComplete}
            onPersonalizationCompleted={onPersonalizationComplete}
            typeId={typeid}
            token={token}
            formType={formType}
          />
        </Modal>
      )}
      {showPrompt && (
        <Message
          title="Google Maps activated for Trails"
          message="Trails & Directions are only accessible via the Google Maps view."
          onClose={() => setShowPrompt(false)}
        />
      )}
      {showSearchAIPrompt && (
        <SearchAIPrompt locId={locId} tourId={tourid} mapObj={googleMap} />
      )}
    </>
  );
}, areEqualShallow);

export default campusMapContainer;
